import { useCallback, useDeferredValue, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { toast } from "react-toastify";
import { describeOutputs } from "../../../engine";
import { createWorkflow, getWorkflow, updateWorkflow } from "../../../api/workflowServices";
// import { runOnSamples } from "../shared/samples"; // Roadmap 1 (built-in steps)
import { dataHost } from "../data/dataHost";
import { previewRun } from "../data/preview";
import { editorReducer, emptyWorkflow, initialState } from "./editorState";

const DEFINITION_KEYS = ["name", "description", "visibility", "sources", "steps", "outputs"];

const apiError = (error, fallback) => error?.response?.data?.message || error?.message || fallback;

export const useWorkflowEditor = ({ workflowId, projectId }) => {
  const [state, dispatch] = useReducer(editorReducer, initialState);
  const [loading, setLoading] = useState(Boolean(workflowId));
  const [saving, setSaving] = useState(false);
  // The AI conversation on the server; saving the workflow ends it.
  const aiConversation = useRef(null);

  useEffect(() => {
    if (!workflowId) {
      dispatch({ type: "load", workflow: emptyWorkflow(projectId) });
      return;
    }
    let cancelled = false;
    setLoading(true);
    getWorkflow(workflowId)
      .then((response) => !cancelled && dispatch({ type: "load", workflow: response.data }))
      .catch((error) => toast.error(apiError(error, "Could not open the workflow")))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [workflowId, projectId]);

  // Deferred so typing in the inspector stays responsive on large samples.
  const deferredWorkflow = useDeferredValue(state.workflow);
  const deferredFiles = useDeferredValue(state.files);
  // Roadmap 1 ran built-in steps synchronously:
  // const run = useMemo(
  //   () => (deferredWorkflow ? runOnSamples(deferredWorkflow, deferredFiles) : null),
  //   [deferredWorkflow, deferredFiles],
  // );
  // Roadmap 2: code steps run in the data worker and the sandbox (async), so
  // the UI never waits on the data. Only what changes results re-runs them:
  // sources' settings and steps' inputs, code and values (not names, labels
  // or colours). A newer run replaces an older one's result. The run holds
  // summaries; tables show their rows by asking the worker (see DataTable).
  const [run, setRun] = useState(null);
  const [running, setRunning] = useState(false);
  const [runRequest, setRunRequest] = useState(0); // bumped by the Run button
  const runKey = deferredWorkflow
    ? JSON.stringify([
        deferredWorkflow.sources.map(({ label, ...source }) => source),
        deferredWorkflow.steps.map(({ id, type, inputs, params, disabled }) => [id, type, inputs, params, disabled]),
      ])
    : null;
  const latestRun = useRef(0);
  useEffect(() => {
    if (!deferredWorkflow) return;
    const ticket = ++latestRun.current;
    setRunning(true);
    previewRun(deferredWorkflow, deferredFiles)
      .then((result) => ticket === latestRun.current && !result.stale && setRun(result))
      .catch((error) => ticket === latestRun.current && toast.error(apiError(error, "Could not run the workflow")))
      .finally(() => ticket === latestRun.current && setRunning(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runKey, deferredFiles, runRequest]);
  const rerun = useCallback(() => setRunRequest((n) => n + 1), []);
  // Output files as summaries (sheets with row and column counts); the files
  // themselves are built in the worker on download.
  const outputs = useMemo(
    () => (run && deferredWorkflow ? describeOutputs(deferredWorkflow, run.tables) : { files: [], errors: [], errorsById: {} }),
    [run, deferredWorkflow],
  );

  // Files no source uses any more are freed in the worker.
  const loadedKeys = useRef(new Set());
  useEffect(() => {
    const keys = new Set(Object.values(state.files).map((file) => file.key));
    const unused = [...loadedKeys.current].filter((key) => !keys.has(key));
    if (unused.length) dataHost.releaseFiles(unused).catch(() => {});
    loadedKeys.current = keys;
  }, [state.files]);

  // Saves the definition; returns the saved workflow (with _id) or null.
  const save = useCallback(async () => {
    const { workflow } = state;
    const payload = Object.fromEntries(DEFINITION_KEYS.map((key) => [key, workflow[key]]));
    setSaving(true);
    try {
      const response = workflow._id
        ? await updateWorkflow(workflow._id, { ...payload, aiConversationId: aiConversation.current })
        : await createWorkflow({ ...payload, projectID: workflow.projectID, aiConversationId: aiConversation.current });
      aiConversation.current = null;
      dispatch({ type: "saved", workflow: response.data });
      toast.success("Workflow saved");
      return response.data;
    } catch (error) {
      toast.error(apiError(error, "Could not save the workflow"));
      return null;
    } finally {
      setSaving(false);
    }
  }, [state]);

  const reload = useCallback(async () => {
    if (!state.workflow?._id) return;
    const response = await getWorkflow(state.workflow._id);
    dispatch({ type: "load", workflow: response.data });
  }, [state.workflow?._id]);

  return { state, dispatch, run, running, rerun, outputs, loading, saving, save, reload, aiConversation };
};
